import { useState, KeyboardEvent, ChangeEvent, useRef } from 'react'
import { Box, Chip, TextField, TextFieldProps } from '@mui/material'
import { Close } from '@mui/icons-material'

interface EmailInputProps extends Omit<TextFieldProps, 'onChange'> {
  emails: string[]
  onEmailsChange: (emails: string[]) => void
}

const emailRegex = /^[A-Za-z0-9_!#$%&'*+/=?`{|}~^.-]+@[A-Za-z0-9.-]+$/

export default function EmailInput({ emails, onEmailsChange, ...textFieldProps }: EmailInputProps) {
  const [inputValue, setInputValue] = useState('')
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const emailIsValid = inputValue && emailRegex.test(inputValue)

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (['Enter', 'Tab', ' ', ','].includes(event.key) && inputValue.trim()) {
      event.preventDefault()
      if (!emailIsValid) {
        setError(`${inputValue} is an invalid email format`)
        return
      }
      const newEmail = inputValue.trim().toLowerCase()
      if (!emails.includes(newEmail)) {
        onEmailsChange([...emails, newEmail])
      }
      setInputValue('')
      // Focus the input after creating a pill so cursor is visible
      setTimeout(() => {
        inputRef.current?.focus()
      }, 0)
    }
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value)
    setError(null)
  }

  const handleDelete = (emailToDelete: string) => {
    onEmailsChange(emails.filter((email) => email !== emailToDelete))
  }

  const handleChipDelete = (emailToDelete: string) => () => {
    handleDelete(emailToDelete)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <TextField
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...textFieldProps}
        label="Email Addresses"
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        inputRef={inputRef}
        multiline
        minRows={1}
        maxRows={6}
      />
      {error && <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>{error}</Box>}
      {emails.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            padding: '8px 0'
          }}
        >
          {emails.map((email) => (
            <Chip
              key={email}
              label={email}
              deleteIcon={<Close />}
              onDelete={handleChipDelete(email)}
              size="small"
              sx={{
                pointerEvents: 'auto',
                backgroundColor: '#f0f0f0',
                '& .MuiChip-deleteIcon': {
                  color: '#666',
                  '&:hover': {
                    color: '#333'
                  }
                }
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}
